import React, { useMemo, useState, useImperativeHandle, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';

import { getLotteryInfo, getWinner, checkIsQualifyToGetWinner } from 'api/client';
import * as clientErrTypes from 'config/clientErrorTypes';
import {
    isCanSetVerifyMechanismGames,
    isForceVerifyMechanismGames,
    isPurePrizeGames,
    pureGameTypeMappingLotteryType
} from 'config/promoGameTypes';
import {
    isLotteryHasAnimation,
    lotteryParticipantTypes,
    lotteryGeneralAnimationType
} from 'config/promoLotteryTypes';
import { gameRunningStatuses } from 'config/gameRunningStatuses';
import { isGetWinnerEmptySuspendError } from 'client/utils/errorObjectChecker';
import { updateParticipantRemainder } from 'client/redux/modules/lotteryParticipant';
import { handlers } from 'client/redux/modules/systemErrorDialog';
import { suspendPromo } from 'client/redux/modules/promoState';
import { updateFlowOfEntryRecord, entryFlowKeys } from 'client/redux/modules/userGameResultRecords';
import { successGetWinner } from 'client/redux/modules/userEntryState';
import {
    setCurrentPrizeUUID,
    setCurrentUnanimationEntriesUUID,
    setGameAlreadyToPlay
} from 'client/redux/modules/purePrizeState';
import { setShouldUpdateUserWinnerList, setAnimatingLotteries } from 'client/redux/modules/flags';

import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import useClientHistory, { JOIN } from 'client/hooks/useClientHistory';

import DialogParticipantPrizeEmpty from 'client/components/Result/DialogParticipantPrizeEmpty';
import WinnerButton from 'client/components/Result/Actions/WinnerButton';
import usePrizeRuleChecker from 'client/hooks/usePrizeRuleChecker';
import { updateFlowOfMGMEntryRecord } from 'client/redux/modules/mgmReferral';
import { isObject } from 'utils/objectManipulate';

function OpenWinnerButton(
    {
        onOpenWinner = () => {},
        openWinnerListChangeCallback = () => {},
        enableGetWinnerByApi,
        btnStyle
    },
    ref
) {
    const dispatch = useDispatch();
    const history = useClientHistory();
    const errHandler = useClientErrorHandler();
    const { isMgmShare, isMgmSelf } = useSelector(s => s.flags);
    const { unOpenWinner, unOpenWinnerOfLotteryPickWinner } = useCategorizedUnOpenWinner(false);
    const {
        unOpenWinner: unOpenMgmWinner,
        unOpenWinnerOfLotteryPickWinner: unOpenMgmWinnerOfLotteryPickWinner
    } = useCategorizedUnOpenWinner(true);
    const {
        isLotteryParticipantRequiredAllCorrect,
        isLotteryInstantRequiredAllCorrect,
        checkIsGameChallengeAllCorrect,
        canClaimPrize
    } = usePrizeRuleChecker();
    const { uuid: promoUUID, type: gameType } = useSelector(s => s.promo);
    const { enableVerifyMechanism } = useSelector(s => s.settingPromo);
    const { gameRunningStatus } = useSelector(s => s.promoState);
    const currentEntryUUID = useSelector(s => s.userEntryState.currentEntryUUID);
    const userGameResultRecords = useSelector(s => s.userGameResultRecords);
    const { userGameResultRecords: mgmUserGameResultRecords } = useSelector(s => s.mgmReferral);
    const lotteryParticipant = useSelector(s => s.lotteryParticipant);
    const lotteryInstant = useSelector(s => s.lotteryInstant);
    const {
        lotteryInstant: mgmLotteryInstant = {},
        lotteryParticipant: mgmLotteryParticipant = {},
        lotteryTasks: mgmLotteryTasks = {}
    } = useSelector(s => s.mgmReferral);
    const lotteryTasks = useSelector(s => s.lotteryTasks);
    const { mgmReferral: { uuid: mgmPromoUUID, type: mgmPromoType } = {} } = useSelector(
        s => s.mgmRelation
    );
    const hasEnableLotteryParticipant = Boolean(lotteryParticipant.uuid);
    const hasEnableMgmLotteryParticipant = Boolean(mgmLotteryParticipant.uuid);
    const hasEnableLotteryInstant = Boolean(lotteryInstant.uuid);
    const hasEnableMgmLotteryInstant = Boolean(mgmLotteryInstant.uuid);
    const hasEnableSignupForm = useSelector(s => Boolean(s.signUpForm.id));
    const hasEnableLotteryTasks = Object.keys(lotteryTasks).length > 0;
    const hasEnableMgmLotteryTasks = Object.keys(mgmLotteryTasks).length > 0;
    const isPurePrizeGame = isPurePrizeGames(gameType);
    const isVerifyToGetWinnerGame =
        isForceVerifyMechanismGames(gameType) ||
        (isCanSetVerifyMechanismGames(gameType) && Boolean(enableVerifyMechanism));
    const isPickWinnerLotteryParticipant =
        lotteryParticipant.type === lotteryParticipantTypes.PICK_THE_PRIZE ||
        mgmLotteryParticipant.type === lotteryParticipantTypes.PICK_THE_PRIZE;
    const [openWinnerEmptyDialog, setOpenWinnerEmptyDialog] = useState(false);

    let hasPendingVerifyEntry = false;

    const getWinnerList = ({
        promoUUID,
        promoType,
        lotteryInstant,
        lotteryParticipant,
        lotteryTasks,
        hasEnableLotteryInstant,
        hasEnableLotteryParticipant,
        isMgmWinner = false,
        unOpenWinner,
        unOpenWinnerOfLotteryPickWinner
    }) => {
        let listGetInstantWinnerAndOpen = [];
        let listGetParticipantWinnerAndOpen = [];
        let listContinueParticipantPickWinner = [];
        let listContinueTaskPickWinner = [];

        const targetUserGameResultRecords = isMgmWinner
            ? mgmUserGameResultRecords
            : userGameResultRecords;

        const otherUserGameResultRecords = isMgmWinner
            ? userGameResultRecords
            : mgmUserGameResultRecords;

        if (enableGetWinnerByApi) {
            for (const entryRecord of targetUserGameResultRecords) {
                // 一般模組要去判斷 qualify 、 deletedAt
                const { deletedAt, flow, gameResult } = entryRecord;
                const parseGameResult = isObject(gameResult) ? gameResult : JSON.parse(gameResult);

                const enableOpenWinnerParticipantLotteryPrize = canClaimPrize(
                    isLotteryParticipantRequiredAllCorrect,
                    checkIsGameChallengeAllCorrect({ type: promoType, gameResult: parseGameResult })
                );
                const enableOpenWinnerInstantLotteryPrize = canClaimPrize(
                    isLotteryInstantRequiredAllCorrect,
                    checkIsGameChallengeAllCorrect({ type: promoType, gameResult: parseGameResult })
                );

                const qualifyStatus = !isVerifyToGetWinnerGame
                    ? entryQualifyStatus.PASS
                    : Boolean(flow.qualified)
                    ? entryQualifyStatus.PASS
                    : entryQualifyStatus.PENDING;

                // MGM 要去判斷原本的 GameResult 是否全被刪除
                const isDeleted = isMgmWinner
                    ? otherUserGameResultRecords.every(obj => obj.deletedAt !== null)
                    : Boolean(deletedAt);

                const hasFulfillSignupForm = !hasEnableSignupForm || Boolean(flow.formSignUp);
                const hasGameResult = Boolean(flow.gameResult);

                // MGM 不用判斷填表
                const isEntryCanJoinLottery = isMgmWinner
                    ? !isDeleted && hasGameResult
                    : !isDeleted && hasGameResult && hasFulfillSignupForm;

                hasPendingVerifyEntry = isMgmWinner
                    ? isEntryCanJoinLottery
                    : isEntryCanJoinLottery && qualifyStatus === entryQualifyStatus.PENDING;

                const isPassEntryCanJoinLottery = isMgmWinner
                    ? isEntryCanJoinLottery
                    : isEntryCanJoinLottery && qualifyStatus === entryQualifyStatus.PASS;

                if (isPassEntryCanJoinLottery) {
                    // 確認是否已配開完動畫獎 (已有 flow lotteryParticipant or 還有待配開的獎項(hasEnableLotteryParticipant))
                    const hasFulfillLotteryParticipant =
                        !hasEnableLotteryParticipant || Boolean(flow.lotteryParticipant);
                    // 確認是否已配開完即時獎 (已有 flow lotteryInstant or 還有待配開的獎項(hasEnableLotteryInstant))
                    const hasFulfillLotteryInstant =
                        !hasEnableLotteryInstant || Boolean(flow.lotteryInstant);
                    // 確認是否已配開完關卡獎 (已有 flow lotteryInstant or 還有待配開的獎項(hasEnableLotteryInstant))
                    const hasFulfillLotteryTask =
                        (isMgmWinner ? !hasEnableMgmLotteryTasks : !hasEnableLotteryTasks) ||
                        Boolean(flow.lotteryTask);

                    if (!hasFulfillLotteryInstant) {
                        // 確認若有開全對才給獎，game result 的答題紀錄需要全對才能往下執行領獎
                        // 沒開也可以直接領獎

                        if (enableOpenWinnerInstantLotteryPrize) {
                            // 如果遇到獎品發完，活動暫停，客戶補獎後，user 可以重新領取畫面會出現 【領取獎品】的功能
                            if (checkIfLotteryHasRemainPrize(lotteryInstant)) {
                                listGetInstantWinnerAndOpen.push({
                                    lotteryType: lotteryInstant.type,
                                    handler: async () => {
                                        try {
                                            const prizeUUID = await getWinnerPrizeByLottery({
                                                promoUUID,
                                                entryUUID: entryRecord.uuid,
                                                lotteryUUID: lotteryInstant.uuid
                                            });
                                            if (prizeUUID) {
                                                const updateUserEntryState = isMgmWinner
                                                    ? updateFlowOfMGMEntryRecord
                                                    : updateFlowOfEntryRecord;
                                                dispatch(
                                                    updateUserEntryState({
                                                        entryUUID: entryRecord.uuid,
                                                        key: entryFlowKeys.LOTTERY_INSTANT
                                                    })
                                                );
                                                dispatch(setShouldUpdateUserWinnerList());
                                                if (entryRecord.uuid === currentEntryUUID) {
                                                    dispatch(successGetWinner(lotteryInstant.type));
                                                }
                                                await openWinner({
                                                    promoUUID,
                                                    entryUUID: entryRecord.uuid,
                                                    prizeUUID,
                                                    lotteryUUID: lotteryInstant.uuid,
                                                    lotteryType: lotteryInstant.type
                                                    // lotteryInstant
                                                });
                                            }
                                        } catch (err) {
                                            errHandler({
                                                err,
                                                handler: handlers.RELOAD
                                            });
                                        }
                                    }
                                });
                            }
                        }
                    }

                    if (!hasFulfillLotteryParticipant) {
                        if (isPickWinnerLotteryParticipant) {
                            if (enableOpenWinnerParticipantLotteryPrize) {
                                // 如果遇到獎品發完，活動暫停，客戶補獎後，user 可以重新領取畫面會出現 【選擇獎品】的功能
                                if (checkIfPickWinnerLotteryHasRemainPrize(lotteryParticipant)) {
                                    // 即時獎是自由選且未完成的話，通過該 button 進入
                                    listContinueParticipantPickWinner.push({
                                        lotteryType: lotteryParticipant.type,
                                        handler: () =>
                                            openWinner({
                                                promoUUID,
                                                entryUUID: entryRecord.uuid,
                                                lotteryUUID: lotteryParticipant.uuid,
                                                lotteryType: lotteryParticipant.type,
                                                lotteryParticipant
                                            })
                                    });
                                }
                            }
                        } else {
                            if (enableOpenWinnerParticipantLotteryPrize) {
                                // 如果遇到獎品發完，活動暫停，客戶補獎後，user 可以重新領取畫面會出現 【試手氣】的功能
                                if (checkIfLotteryHasRemainPrize(lotteryParticipant)) {
                                    // 參加獎非自由選，能夠通過該 button 配審核成功獎、補配
                                    listGetParticipantWinnerAndOpen.push({
                                        lotteryType: lotteryParticipant.type,
                                        handler: async () => {
                                            try {
                                                const prizeUUID = await getWinnerPrizeByLottery({
                                                    promoUUID,
                                                    entryUUID: entryRecord.uuid,
                                                    lotteryUUID: lotteryParticipant.uuid
                                                });
                                                if (prizeUUID) {
                                                    const updateUserEntryState = isMgmWinner
                                                        ? updateFlowOfMGMEntryRecord
                                                        : updateFlowOfEntryRecord;
                                                    dispatch(
                                                        updateUserEntryState({
                                                            entryUUID: entryRecord.uuid,
                                                            key: entryFlowKeys.LOTTERY_PARTICIPANT
                                                        })
                                                    );
                                                    dispatch(setShouldUpdateUserWinnerList());
                                                    if (entryRecord.uuid === currentEntryUUID) {
                                                        dispatch(
                                                            successGetWinner(
                                                                lotteryParticipant.type
                                                            )
                                                        );
                                                    }
                                                    await openWinner({
                                                        promoUUID,
                                                        entryUUID: entryRecord.uuid,
                                                        prizeUUID,
                                                        lotteryUUID: lotteryParticipant.uuid,
                                                        lotteryType: lotteryParticipant.type,
                                                        lotteryParticipant
                                                    });
                                                }
                                            } catch (err) {
                                                errHandler({
                                                    err,
                                                    handler: handlers.RELOAD
                                                });
                                            }
                                        }
                                    });
                                }
                            }
                        }
                    }
                    // ALAN TODO: 不確定要不要補上 instant
                    if (!hasFulfillLotteryTask) {
                        const unOpenTaskPickWinnerPrize = unOpenWinnerOfLotteryPickWinner.find(
                            w =>
                                w.entryUuid === entryRecord.uuid &&
                                lotteryTasks[w.prize.lotteryUuid] !== undefined
                        );
                        // 如果關卡獎自由選配到一半且未完成的話，從該按鈕進入
                        if (
                            unOpenTaskPickWinnerPrize &&
                            checkIfPickWinnerLotteryHasRemainPrize(
                                lotteryTasks[unOpenTaskPickWinnerPrize.prize.lotteryUuid]
                            )
                        ) {
                            listContinueTaskPickWinner.push({
                                lotteryType: unOpenTaskPickWinnerPrize.type,
                                handler: () =>
                                    openWinner({
                                        promoUUID,
                                        entryUUID: entryRecord.uuid,
                                        lotteryUUID: unOpenTaskPickWinnerPrize.prize.lotteryUuid,
                                        lotteryType: unOpenTaskPickWinnerPrize.type,
                                        lotteryParticipant
                                    })
                            });
                        }
                    }
                }
            }
        }

        const listOpenWinner = unOpenWinner.map(w => ({
            lotteryType: w.lotteryType,
            handler: () =>
                openWinner({
                    promoUUID,
                    entryUUID: w.entryUuid,
                    prizeUUID: w.prize.uuid,
                    lotteryUUID: w.prize.lotteryUuid,
                    lotteryType: w.lotteryType,
                    lotteryParticipant
                    // lotteryInstant
                })
        }));

        // 活動不能再參加（暫停、結束）的話，未完成的自由選已領取的獎品要讓 user 開
        const listOpenWinnerOfUnCompletePickWinner = (gameRunningStatus ===
        gameRunningStatuses.RUNNING
            ? []
            : unOpenWinnerOfLotteryPickWinner
        ).map(w => ({
            lotteryType: lotteryGeneralAnimationType,
            handler: () =>
                toLotteryAnimation({
                    entryUUID: w.entryUuid,
                    prizeUUID: w.prize.uuid,
                    animatingLottery: {
                        uuid: w.prize.lotteryUuid,
                        type: lotteryGeneralAnimationType
                    }
                })
        }));
        return {
            openWinnerList: [
                ...listGetInstantWinnerAndOpen,
                ...listGetParticipantWinnerAndOpen,
                ...listOpenWinner,
                ...listOpenWinnerOfUnCompletePickWinner
            ],
            pickWinnerList: [...listContinueParticipantPickWinner, ...listContinueTaskPickWinner]
        };
    };

    const mgmWinnerList =
        isMgmShare || isMgmSelf
            ? getWinnerList({
                  promoUUID: mgmPromoUUID,
                  promoType: mgmPromoType,
                  lotteryInstant: mgmLotteryInstant,
                  lotteryParticipant: mgmLotteryParticipant,
                  lotteryTasks: mgmLotteryTasks,
                  hasEnableLotteryInstant: hasEnableMgmLotteryInstant,
                  hasEnableLotteryParticipant: hasEnableMgmLotteryParticipant,
                  isMgmWinner: true,
                  unOpenWinner: unOpenMgmWinner,
                  unOpenWinnerOfLotteryPickWinner: unOpenMgmWinnerOfLotteryPickWinner
              })
            : { openWinnerList: [], pickWinnerList: [] };

    const winnerList = getWinnerList({
        promoUUID: promoUUID,
        promoType: gameType,
        lotteryInstant: lotteryInstant,
        lotteryParticipant: lotteryParticipant,
        lotteryTasks: lotteryTasks,
        hasEnableLotteryInstant: hasEnableLotteryInstant,
        hasEnableLotteryParticipant: hasEnableLotteryParticipant,
        isMgmWinner: false,
        unOpenWinner: unOpenWinner,
        unOpenWinnerOfLotteryPickWinner: unOpenWinnerOfLotteryPickWinner
    });

    const openWinnerList = [...winnerList.openWinnerList, ...mgmWinnerList.openWinnerList];
    const pickWinnerList = [...winnerList.pickWinnerList, ...mgmWinnerList.pickWinnerList];
    // 送去 Lottery 頁做開獎
    function toLotteryAnimation({ entryUUID, prizeUUID = '', animatingLottery }) {
        onOpenWinner({
            entriesUUID: entryUUID,
            prizeUUID,
            animatingLottery
        });
    }
    async function openWinner({
        promoUUID,
        entryUUID,
        prizeUUID = '',
        lotteryUUID,
        lotteryType,
        // lotteryInstant = {},
        lotteryParticipant = {}
    }) {
        // 純獎模組的動畫獎直接回 join 流程遊玩
        if (
            isPurePrizeGame &&
            Boolean(
                pureGameTypeMappingLotteryType[gameType] === lotteryType &&
                    lotteryUUID === lotteryParticipant.uuid
            )
        ) {
            dispatch(setCurrentPrizeUUID(prizeUUID));
            dispatch(setCurrentUnanimationEntriesUUID(entryUUID));
            dispatch(setGameAlreadyToPlay());
            dispatch(
                setAnimatingLotteries({
                    type: lotteryType,
                    uuid: lotteryUUID,
                    animationSettings: lotteryParticipant.AnimationSettings
                })
            );
            history.push(JOIN);
        } else {
            // 非純獎模組的動畫獎，前往領取獎品動畫頁(/lottery)做開獎
            toLotteryAnimation({
                entryUUID,
                prizeUUID,
                animatingLottery: await getAnimatingLottery({
                    lotteryUUID,
                    lotteryType,
                    lotteryParticipant,
                    promoUUID
                })
            });
        }
    }
    async function getAnimatingLottery({
        lotteryUUID,
        lotteryType,
        lotteryParticipant,
        promoUUID
    }) {
        if (!isLotteryHasAnimation(lotteryType)) {
            return { type: lotteryType, uuid: lotteryUUID };
        } else if (lotteryUUID === lotteryParticipant.uuid) {
            return {
                type: lotteryType,
                uuid: lotteryUUID,
                AnimationSettings: lotteryParticipant.AnimationSettings
            };
        } else {
            const {
                payload: { lotterySetting: { AnimationSettings } = {} } = {}
            } = await getLotteryInfo(promoUUID, lotteryUUID);
            return { type: lotteryType, uuid: lotteryUUID, AnimationSettings };
        }
    }
    // 拿 Lottery 去做配獎，並回傳配到的獎品
    async function getWinnerPrizeByLottery({ promoUUID, entryUUID, lotteryUUID }) {
        await checkIsQualifyToGetWinner({
            promoUUID: promoUUID,
            entriesUUID: entryUUID,
            lotteryUUID
        });
        const winnerResp = await getWinner(promoUUID, entryUUID, lotteryUUID);
        const { httpStatus, payload } = winnerResp;
        if (httpStatus === 200) {
            const prizeUUID = payload[0];
            return prizeUUID;
        } else if (httpStatus === 226) {
            setOpenWinnerEmptyDialog(true);
            return null;
        } else if (isGetWinnerEmptySuspendError(winnerResp)) {
            dispatch(suspendPromo());
            setOpenWinnerEmptyDialog(true);
            return null;
        } else if (httpStatus === 400) {
            if (payload.message === clientErrTypes.POINTS_IS_NOT_ENOUGH_BY_PRE_CHECK) {
                throw generateApiErrObj(
                    winnerResp,
                    clientErrTypes.POINTS_IS_NOT_ENOUGH_BY_PRE_CHECK
                );
            } else if (payload.message === clientErrTypes.POINTS_IS_NOT_ENOUGH) {
                throw generateApiErrObj(winnerResp, clientErrTypes.POINTS_IS_NOT_ENOUGH);
            } else {
                throw generateApiErrObj(winnerResp, clientErrTypes.GET_WINNER);
            }
        } else {
            throw generateApiErrObj(winnerResp, clientErrTypes.GET_WINNER);
        }
    }
    function checkIfPickWinnerLotteryHasRemainPrize(lottery = {}) {
        const { remainder, prizeLimited } = lottery;
        return !prizeLimited || remainder > 0;
    }
    function checkIfLotteryHasRemainPrize(lottery = {}) {
        const { remainder, prizeLimited, suspendWhenNoPrize } = lottery;
        if (suspendWhenNoPrize) {
            return true;
        } else {
            return !prizeLimited || remainder > 0;
        }
    }

    const shouldShowButton = openWinnerList.length + pickWinnerList.length > 0;
    const winnerListLength = openWinnerList.length || pickWinnerList.length;
    useImperativeHandle(ref, () => ({
        getOpenWinnerButtonData: () => ({
            shouldShowButton,
            hasEnableLotteryParticipant:
                hasEnableLotteryParticipant || hasEnableMgmLotteryParticipant,
            hasEnableLotteryInstant: hasEnableLotteryInstant || hasEnableMgmLotteryInstant,
            hasPendingVerifyEntry,
            openWinnerList,
            pickWinnerList
        })
    }));

    // To sync ref data from children (OpenWinnerButton) to parent component (ResultHeader)
    useEffect(() => {
        openWinnerListChangeCallback(winnerListLength);
    }, [openWinnerListChangeCallback, winnerListLength]);

    return (
        <>
            {openWinnerList.length > 0 && (
                <WinnerButton
                    lotteryType={openWinnerList[0]?.lotteryType}
                    onClick={openWinnerList[0]?.handler}
                    btnStyle={btnStyle}
                />
            )}

            {pickWinnerList.length > 0 && (
                <WinnerButton
                    lotteryType={pickWinnerList[0]?.lotteryType}
                    onClick={pickWinnerList[0]?.handler}
                    btnStyle={btnStyle}
                />
            )}
            <DialogParticipantPrizeEmpty
                title={i18n.t('label.check_in.lottery_task_card.prize_empty')}
                desc={i18n.t('desc.prize_error.prizes_is_empty')}
                isOpen={openWinnerEmptyDialog}
                onCloseHandler={() => {
                    setOpenWinnerEmptyDialog(false);
                    dispatch(updateParticipantRemainder(0));
                }}
            />
        </>
    );
}

export default React.forwardRef(OpenWinnerButton);

const entryQualifyStatus = {
    PASS: 'passed',
    PENDING: 'pending'
};

function useCategorizedUnOpenWinner(isMgmWinner) {
    const { unOpenWinnerPrize = [] } = useSelector(s => s.userWinnerPrize);
    const { userWinnerPrize: { unOpenWinnerPrize: mgmUnOpenWinnerPrize = [] } = {} } = useSelector(
        s => s.mgmReferral
    );
    const currentUnOpenWinnerPrize = isMgmWinner ? mgmUnOpenWinnerPrize : unOpenWinnerPrize;

    return useMemo(() => {
        const validUnOpenWinner = currentUnOpenWinnerPrize.filter(w => !w.deletedAt);
        return {
            unOpenWinner: validUnOpenWinner.filter(
                w => w.lotteryType !== lotteryParticipantTypes.PICK_THE_PRIZE
            ),
            unOpenWinnerOfLotteryPickWinner: validUnOpenWinner.filter(
                w => w.lotteryType === lotteryParticipantTypes.PICK_THE_PRIZE
            )
        };
    }, [currentUnOpenWinnerPrize]);
}
